// Legacy placement metadata only. Never a source of GPU display geometry.
// Kept separate so an isolated migration audit need not load the application.
function positiveNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

export function parseImportedSvgLengthMm(value) {
  const match = String(value || '').trim().match(/^(-?\d+(?:\.\d+)?)(mm)?$/i);
  if (!match) return null;
  return positiveNumber(match[1]);
}

export function calculateImportedSvgContentBounds(root) {
  if (!root) return null;
  let bounds = null;
  const walk = (node, matrix) => {
    if (!node || node.nodeType !== 1) return;
    const currentMatrix = multiplyImportedSvgMatrix(matrix, parseImportedSvgTransform(node.getAttribute('transform')));
    const tag = String(node.tagName || '').toLowerCase();
    if (tag === 'path') {
      bounds = mergeImportedSvgBounds(bounds, transformImportedSvgBounds(parseImportedPathBounds(node.getAttribute('d')), currentMatrix));
    } else if (tag === 'rect') {
      const x = Number(node.getAttribute('x')) || 0;
      const y = Number(node.getAttribute('y')) || 0;
      const width = Number(node.getAttribute('width')) || 0;
      const height = Number(node.getAttribute('height')) || 0;
      if (width > 0 && height > 0) {
        bounds = mergeImportedSvgBounds(bounds, transformImportedSvgBounds({
          minX: x,
          minZ: y,
          maxX: x + width,
          maxZ: y + height
        }, currentMatrix));
      }
    } else if (tag === 'circle' || tag === 'ellipse') {
      const cx = Number(node.getAttribute('cx')) || 0;
      const cy = Number(node.getAttribute('cy')) || 0;
      const rx = Number(node.getAttribute(tag === 'circle' ? 'r' : 'rx')) || 0;
      const ry = Number(node.getAttribute(tag === 'circle' ? 'r' : 'ry')) || 0;
      if (rx > 0 && ry > 0) {
        bounds = mergeImportedSvgBounds(bounds, transformImportedSvgBounds({
          minX: cx - rx,
          minZ: cy - ry,
          maxX: cx + rx,
          maxZ: cy + ry
        }, currentMatrix));
      }
    } else if (tag === 'line') {
      const x1 = Number(node.getAttribute('x1')) || 0;
      const y1 = Number(node.getAttribute('y1')) || 0;
      const x2 = Number(node.getAttribute('x2')) || 0;
      const y2 = Number(node.getAttribute('y2')) || 0;
      bounds = mergeImportedSvgBounds(bounds, transformImportedSvgBounds({
        minX: Math.min(x1, x2),
        minZ: Math.min(y1, y2),
        maxX: Math.max(x1, x2),
        maxZ: Math.max(y1, y2)
      }, currentMatrix));
    }
    Array.from(node.children || []).forEach(child => walk(child, currentMatrix));
  };
  walk(root, { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });
  if (!bounds) return null;
  const width = bounds.maxX - bounds.minX;
  const depth = bounds.maxZ - bounds.minZ;
  return width > 0 && depth > 0
    ? { minX: bounds.minX, minZ: bounds.minZ, width, depth }
    : null;
}

function parseImportedSvgTransform(value) {
  const raw = String(value || '').trim();
  if (!raw) return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  let matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  const regex = /([a-z]+)\(([^)]*)\)/gi;
  let match;
  while ((match = regex.exec(raw))) {
    const name = match[1].toLowerCase();
    const values = match[2].trim().split(/[\s,]+/).map(Number).filter(Number.isFinite);
    let next = null;
    if (name === 'matrix' && values.length >= 6) {
      next = { a: values[0], b: values[1], c: values[2], d: values[3], e: values[4], f: values[5] };
    } else if (name === 'translate') {
      next = { a: 1, b: 0, c: 0, d: 1, e: values[0] || 0, f: values[1] || 0 };
    } else if (name === 'scale') {
      const sx = Number.isFinite(values[0]) ? values[0] : 1;
      const sy = Number.isFinite(values[1]) ? values[1] : sx;
      next = { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
    } else if (name === 'rotate') {
      const angle = (values[0] || 0) * Math.PI / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const rotate = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
      next = values.length >= 3
        ? multiplyImportedSvgMatrix(
          multiplyImportedSvgMatrix({ a: 1, b: 0, c: 0, d: 1, e: values[1], f: values[2] }, rotate),
          { a: 1, b: 0, c: 0, d: 1, e: -values[1], f: -values[2] }
        )
        : rotate;
    }
    if (next) matrix = multiplyImportedSvgMatrix(matrix, next);
  }
  return matrix;
}

function multiplyImportedSvgMatrix(left, right) {
  return {
    a: left.a * right.a + left.c * right.b,
    b: left.b * right.a + left.d * right.b,
    c: left.a * right.c + left.c * right.d,
    d: left.b * right.c + left.d * right.d,
    e: left.a * right.e + left.c * right.f + left.e,
    f: left.b * right.e + left.d * right.f + left.f
  };
}

function transformImportedSvgPoint(point, matrix) {
  return {
    x: matrix.a * point.x + matrix.c * point.z + matrix.e,
    z: matrix.b * point.x + matrix.d * point.z + matrix.f
  };
}

function transformImportedSvgBounds(bounds, matrix) {
  if (!bounds) return null;
  const points = [
    transformImportedSvgPoint({ x: bounds.minX, z: bounds.minZ }, matrix),
    transformImportedSvgPoint({ x: bounds.maxX, z: bounds.minZ }, matrix),
    transformImportedSvgPoint({ x: bounds.maxX, z: bounds.maxZ }, matrix),
    transformImportedSvgPoint({ x: bounds.minX, z: bounds.maxZ }, matrix)
  ];
  return {
    minX: Math.min(...points.map(point => point.x)),
    minZ: Math.min(...points.map(point => point.z)),
    maxX: Math.max(...points.map(point => point.x)),
    maxZ: Math.max(...points.map(point => point.z))
  };
}

function mergeImportedSvgBounds(current, next) {
  if (!next) return current;
  if (!current) return next;
  return {
    minX: Math.min(current.minX, next.minX),
    minZ: Math.min(current.minZ, next.minZ),
    maxX: Math.max(current.maxX, next.maxX),
    maxZ: Math.max(current.maxZ, next.maxZ)
  };
}

function parseImportedPathBounds(d) {
  const tokens = String(d || '').match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi) || [];
  let index = 0;
  let command = '';
  let x = 0;
  let z = 0;
  let startX = 0;
  let startZ = 0;
  let bounds = null;
  const isCommand = token => /^[a-zA-Z]$/.test(token);
  const numberAt = () => Number(tokens[index++]);
  const addPoint = (px, pz) => {
    if (!Number.isFinite(px) || !Number.isFinite(pz)) return;
    bounds = mergeImportedSvgBounds(bounds, { minX: px, minZ: pz, maxX: px, maxZ: pz });
  };
  while (index < tokens.length) {
    if (isCommand(tokens[index])) command = tokens[index++];
    if (!command) break;
    const relative = command === command.toLowerCase();
    const type = command.toUpperCase();
    if (type === 'Z') {
      x = startX;
      z = startZ;
      addPoint(x, z);
      command = '';
      continue;
    }
    if (type === 'H') {
      while (index < tokens.length && !isCommand(tokens[index])) {
        const value = numberAt();
        x = relative ? x + value : value;
        addPoint(x, z);
      }
      continue;
    }
    if (type === 'V') {
      while (index < tokens.length && !isCommand(tokens[index])) {
        const value = numberAt();
        z = relative ? z + value : value;
        addPoint(x, z);
      }
      continue;
    }
    const pairCounts = { M: 1, L: 1, T: 1, S: 2, Q: 2, C: 3, A: 1 };
    const pairCount = pairCounts[type] || 0;
    if (!pairCount) break;
    while (index < tokens.length && !isCommand(tokens[index])) {
      if (type === 'A') {
        if (index + 6 >= tokens.length) break;
        index += 5;
        const px = numberAt();
        const pz = numberAt();
        x = relative ? x + px : px;
        z = relative ? z + pz : pz;
        addPoint(x, z);
        continue;
      }
      for (let pairIndex = 0; pairIndex < pairCount; pairIndex += 1) {
        if (index + 1 >= tokens.length) break;
        const px = numberAt();
        const pz = numberAt();
        const nextX = relative ? x + px : px;
        const nextZ = relative ? z + pz : pz;
        addPoint(nextX, nextZ);
        if (pairIndex === pairCount - 1) {
          x = nextX;
          z = nextZ;
          if (type === 'M') {
            startX = x;
            startZ = z;
            command = relative ? 'l' : 'L';
          }
        }
      }
    }
  }
  return bounds;
}

